import React, { useEffect, useState } from "react";
import { FaTimes, FaChevronDown, FaChevronUp } from "react-icons/fa";

// ─── Field wrapper ────────────────────────────────────────────────────────────
const Field = ({ label, children, error, hint }) => (
  <div>
    <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-gray-400">
      {label}
    </label>
    {children}
    {hint && !error && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
    {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
  </div>
);

const inputCls =
  "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 placeholder-gray-300 outline-none transition-colors focus:border-pink-400 focus:ring-2 focus:ring-pink-100";

// ─── Toggle chip ──────────────────────────────────────────────────────────────
const Chip = ({ label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
      active
        ? "border-pink-400 bg-pink-50 text-pink-700"
        : "border-gray-200 bg-white text-gray-500 hover:border-gray-300 hover:bg-gray-50"
    }`}
  >
    {label}
  </button>
);

// ─── Constants ────────────────────────────────────────────────────────────────
const GENDER_OPTIONS = ["male", "female", "other"];
const DAY_OPTIONS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];
const today = new Date().toISOString().slice(0, 10);

const initialForm = {
  couponCode: "",
  discountPercentage: "",
  validFrom: today,
  expiryDate: "",
  usageLimit: "",
  status: "active",
  genders: [],
  preferredLanguages: "",
  phoneNumbers: "",
  applicableDays: [],
  // weekendOnly removed — use Saturday + Sunday chips instead
};

// ─── Main Modal ───────────────────────────────────────────────────────────────
const CouponModal = ({ isOpen, onClose, onSubmit, editData, loading }) => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [targetingOpen, setTargetingOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (editData) {
      const targeting = editData.targeting || {};

      // If legacy weekendOnly was true, seed Saturday + Sunday into applicableDays
      let applicableDays = targeting.applicableDays || [];
      if (targeting.weekendOnly) {
        const weekendDays = ["Saturday", "Sunday"];
        applicableDays = [...new Set([...applicableDays, ...weekendDays])];
      }

      const populated = {
        couponCode: editData.couponCode || "",
        discountPercentage: editData.discountPercentage ?? "",
        validFrom: editData.validFrom
          ? new Date(editData.validFrom).toISOString().slice(0, 10)
          : today,
        expiryDate: editData.expiryDate
          ? new Date(editData.expiryDate).toISOString().slice(0, 10)
          : "",
        usageLimit: editData.usageLimit ?? "",
        status: editData.status || "active",
        genders: targeting.genders || [],
        preferredLanguages: (targeting.preferredLanguages || []).join(", "),
        phoneNumbers: (targeting.phoneNumbers || []).join(", "),
        applicableDays,
      };
      setForm(populated);
      const hasTargeting =
        populated.genders.length > 0 ||
        populated.preferredLanguages ||
        populated.phoneNumbers ||
        populated.applicableDays.length > 0;
      setTargetingOpen(hasTargeting);
    } else {
      setForm(initialForm);
      setTargetingOpen(false);
    }
    setErrors({});
  }, [editData, isOpen]);

  const validate = () => {
    const e = {};
    if (!form.couponCode.trim()) e.couponCode = "Coupon code is required.";
    const disc = Number(form.discountPercentage);
    if (!form.discountPercentage || disc < 1 || disc > 100)
      e.discountPercentage = "Must be between 1 and 100.";
    if (!form.validFrom) e.validFrom = "Valid-from date is required.";
    if (!form.expiryDate) {
      e.expiryDate = "Expiry date is required.";
    } else if (new Date(form.expiryDate) <= new Date()) {
      e.expiryDate = "Must be a future date.";
    } else if (
      form.validFrom &&
      new Date(form.expiryDate) <= new Date(form.validFrom)
    ) {
      e.expiryDate = "Must be after the valid-from date.";
    }
    if (!form.usageLimit || Number(form.usageLimit) < 1)
      e.usageLimit = "Must be at least 1.";
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const toggleChip = (field, value) => {
    setForm((prev) => {
      const arr = prev[field];
      const next = arr.includes(value)
        ? arr.filter((v) => v !== value)
        : [...arr, value];
      return { ...prev, [field]: next };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    const parseList = (str) =>
      str
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    const payload = {
      couponCode: form.couponCode.trim().toUpperCase(),
      discountPercentage: Number(form.discountPercentage),
      validFrom: form.validFrom,
      expiryDate: form.expiryDate,
      usageLimit: Number(form.usageLimit),
      status: form.status,
      targeting: {
        genders: form.genders,
        preferredLanguages: parseList(form.preferredLanguages),
        phoneNumbers: parseList(form.phoneNumbers),
        applicableDays: form.applicableDays,
        // weekendOnly intentionally omitted — derived from applicableDays on backend if needed
      },
    };
    onSubmit(payload);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="bg-black/25 absolute inset-0 backdrop-blur-[8px]"
        onClick={onClose}
      />

      <div className="relative z-10 w-full max-w-[440px] overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-2xl shadow-gray-200/80">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <div>
            <h2 className="text-sm font-semibold text-gray-800">
              {editData ? "Edit coupon" : "Create coupon"}
            </h2>
            <p className="mt-0.5 text-xs text-gray-400">
              {editData
                ? "Update the details below."
                : "Fill in the details for the new coupon."}
            </p>
          </div>
          <button
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded-lg border border-gray-100 text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-600"
          >
            <FaTimes className="text-[10px]" />
          </button>
        </div>

        {/* Body */}
        <div className="max-h-[68vh] overflow-y-auto">
          <form onSubmit={handleSubmit} id="coupon-form">
            <div className="space-y-4 px-6 py-5">
              {/* Coupon code — always editable now */}
              <Field label="Coupon code *" error={errors.couponCode}>
                <input
                  name="couponCode"
                  value={form.couponCode}
                  onChange={handleChange}
                  placeholder="e.g. SAVE20"
                  className={inputCls}
                />
              </Field>

              {/* Discount + Usage */}
              <div className="grid grid-cols-2 gap-3">
                <Field label="Discount (%) *" error={errors.discountPercentage}>
                  <input
                    type="number"
                    name="discountPercentage"
                    value={form.discountPercentage}
                    onChange={handleChange}
                    min={1}
                    max={100}
                    placeholder="1–100"
                    className={inputCls}
                  />
                </Field>
                <Field label="Usage limit *" error={errors.usageLimit}>
                  <input
                    type="number"
                    name="usageLimit"
                    value={form.usageLimit}
                    onChange={handleChange}
                    min={1}
                    placeholder="e.g. 100"
                    className={inputCls}
                  />
                </Field>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-3">
                <Field label="Valid from *" error={errors.validFrom}>
                  <input
                    type="date"
                    name="validFrom"
                    value={form.validFrom}
                    onChange={handleChange}
                    className={inputCls}
                  />
                </Field>
                <Field label="Expiry date *" error={errors.expiryDate}>
                  <input
                    type="date"
                    name="expiryDate"
                    value={form.expiryDate}
                    onChange={handleChange}
                    min={form.validFrom || today}
                    className={inputCls}
                  />
                </Field>
              </div>

              {/* Status */}
              <Field label="Status">
                <select
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                  className={inputCls}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </Field>

              {/* Targeting */}
              <div className="rounded-xl border border-gray-100 bg-gray-50/50">
                <button
                  type="button"
                  onClick={() => setTargetingOpen((p) => !p)}
                  className="flex w-full items-center justify-between px-4 py-3 text-left"
                >
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Targeting{" "}
                    <span className="font-normal normal-case text-gray-400">
                      (optional)
                    </span>
                  </span>
                  {targetingOpen ? (
                    <FaChevronUp className="text-[9px] text-gray-400" />
                  ) : (
                    <FaChevronDown className="text-[9px] text-gray-400" />
                  )}
                </button>

                {targetingOpen && (
                  <div className="space-y-4 border-t border-gray-100 px-4 pb-4 pt-3">
                    {/* Gender chips */}
                    <Field label="Genders">
                      <div className="flex flex-wrap gap-1.5 pt-0.5">
                        {GENDER_OPTIONS.map((g) => (
                          <Chip
                            key={g}
                            label={g.charAt(0).toUpperCase() + g.slice(1)}
                            active={form.genders.includes(g)}
                            onClick={() => toggleChip("genders", g)}
                          />
                        ))}
                      </div>
                      <p className="mt-1.5 text-xs text-gray-400">
                        Leave empty to target all genders.
                      </p>
                    </Field>

                    {/* Applicable days chips — full week, select Sat+Sun for weekend-only */}
                    <Field label="Applicable days">
                      <div className="flex flex-wrap gap-1.5 pt-0.5">
                        {DAY_OPTIONS.map((d) => (
                          <Chip
                            key={d}
                            label={d.slice(0, 3)}
                            active={form.applicableDays.includes(d)}
                            onClick={() => toggleChip("applicableDays", d)}
                          />
                        ))}
                      </div>
                      <p className="mt-1.5 text-xs text-gray-400">
                        Leave empty to apply on any day. Select{" "}
                        <span className="font-medium text-gray-500">
                          Sat + Sun
                        </span>{" "}
                        for weekend-only.
                      </p>
                    </Field>

                    <Field
                      label="Preferred languages"
                      hint="Comma-separated, e.g. english, hindi"
                    >
                      <input
                        name="preferredLanguages"
                        value={form.preferredLanguages}
                        onChange={handleChange}
                        placeholder="english, hindi, tamil"
                        className={inputCls}
                      />
                    </Field>

                    <Field
                      label="Phone number whitelist"
                      hint="Comma-separated. Leave blank to allow all."
                    >
                      <input
                        name="phoneNumbers"
                        value={form.phoneNumbers}
                        onChange={handleChange}
                        placeholder="+919876543210, +918888888888"
                        className={inputCls}
                      />
                    </Field>
                  </div>
                )}
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="flex gap-2.5 border-t border-gray-100 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-gray-200 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="coupon-form"
            disabled={loading}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-pink-600 py-2 text-sm font-medium text-white transition-colors hover:bg-pink-700 disabled:opacity-60"
          >
            {loading ? (
              <>
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                Saving…
              </>
            ) : editData ? (
              "Update coupon"
            ) : (
              "Create coupon"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CouponModal;
